// Shared option/variant rules for the car configurator.
//
// The product page (FormQuery.vue) and Quick Bid must offer the exact same
// choices, so the grouping and single/multi-select rules live here and are
// imported by both. Kept free of Vue/axios so they stay directly testable.

// Fallback only, for options stored before the sheet's "Multi Allowed" column
// was saved (no multiAllowed on the record). Anything not listed is
// single-select: picking an option replaces the current pick in that
// subcategory (e.g. "Wheels" — a car has one set of wheels).
export const MULTI_SELECT_SUBCATEGORIES = [
  "Wheel Accessory",
  "Audio and Communication",
  "Assistance Systems",
  "Interior Comfort",
  "Seats",
  "Exterior",
  "Exterior Packages",
  "Roof and Transport Systems",
  "Exterior Decals and Logos",
  "Performance",
  "Lights and Vision",
  "Interior",
  "Interior Packages and Trim",
  "Seat Belts and Seat Design",
  "Steering Wheel and Gearshift Lever/Selector",
  "E-Mobility",
];

// The import sheet decides: its "Multi Allowed" column is stored per option
// as `multiAllowed` (and is what the admin catalog shows as Single/Multi).
export function isMultiSelectable(option) {
  if (typeof option?.multiAllowed === "boolean") return option.multiAllowed;
  return MULTI_SELECT_SUBCATEGORIES.includes(option?.subCategory);
}

// Options that compete with `option` in a pick-one group: same section and
// group, and single-select themselves. Multi-select options in the same group
// are left alone, since the sheet can mix the two within one group.
function singleSelectSiblings(option, options) {
  return (options || []).filter(
    (other) =>
      other.subCategory === option.subCategory &&
      other.mainCategory === option.mainCategory &&
      !isMultiSelectable(other)
  );
}

export function isIncludedOption(option) {
  const price = parseInt(option?.price, 10);
  return Number.isNaN(price) || price <= 0;
}

// Included (standard) options render an "Included" badge instead of a
// checkbox/radio; only paid options get an input the buyer can toggle.
export function rendersAsInput(option) {
  return !isIncludedOption(option);
}

// Older name kept as an alias so any stray import keeps working.
export const rendersAsCheckbox = rendersAsInput;

// Included options come with the car: they are preselected and not clickable
// in either single- or multi-select subcategories. Picking a paid sibling in a
// single-select subcategory still replaces the included pick.
export function isOptionDisabled(option) {
  return isIncludedOption(option);
}

export function activeOptions(variants) {
  return (variants || []).filter((v) => v?.typeCategory === "options" && v.isActive);
}

// mainCategory -> subCategory -> options, with the subcategory named after its
// main category first. Options missing either category are dropped.
export function groupOptionsByCategory(options) {
  const grouped = (options || []).reduce((acc, option) => {
    if (!option?.mainCategory || !option?.subCategory) return acc;
    if (!acc[option.mainCategory]) acc[option.mainCategory] = {};
    if (!acc[option.mainCategory][option.subCategory]) {
      acc[option.mainCategory][option.subCategory] = [];
    }
    acc[option.mainCategory][option.subCategory].push(option);
    return acc;
  }, {});

  const sorted = {};
  Object.keys(grouped).forEach((mainCategory) => {
    sorted[mainCategory] = {};
    const subCategories = Object.keys(grouped[mainCategory]);

    subCategories
      .filter((subCategory) => subCategory === mainCategory)
      .forEach((subCategory) => {
        sorted[mainCategory][subCategory] = grouped[mainCategory][subCategory];
      });

    subCategories
      .filter((subCategory) => subCategory !== mainCategory)
      .forEach((subCategory) => {
        const variants = grouped[mainCategory][subCategory];
        const hasNonZeroPrice = variants.some(
          (variant) => parseInt(variant.price, 10) > 0
        );
        const hasIncludedOptions = variants.some((variant) => isIncludedOption(variant));
        if (hasNonZeroPrice || hasIncludedOptions) {
          sorted[mainCategory][subCategory] = variants;
        }
      });
  });

  return sorted;
}

// Options that come with the car are preselected: every included multi-select
// option, and the first included single-select option of each group.
export function autoSelectedOptionIds(options) {
  const ids = [];
  const singleGroupsTaken = new Set();
  (options || [])
    .filter((option) => isIncludedOption(option))
    .forEach((option) => {
      if (isMultiSelectable(option)) {
        ids.push(option._id);
        return;
      }
      const group = `${option.mainCategory}::${option.subCategory}`;
      if (singleGroupsTaken.has(group)) return;
      singleGroupsTaken.add(group);
      ids.push(option._id);
    });
  return ids;
}

// Returns the new selection after clicking `option`. Multi-select subcategories
// add/remove; single-select ones replace whatever else is picked there.
export function toggleOptionSelection(selectedIds, option, options) {
  const ids = Array.isArray(selectedIds) ? selectedIds.slice() : [];
  if (!option?._id) return ids;

  const isSelected = ids.includes(option._id);

  if (isMultiSelectable(option)) {
    if (!isSelected) return ids.concat(option._id);
    if (isIncludedOption(option)) return ids; // freebies stay on
    return ids.filter((id) => id !== option._id);
  }

  const siblings = singleSelectSiblings(option, options);

  if (isSelected) {
    // Deselecting a paid pick falls back to the subcategory's included option
    // (if any), since the included card has no input to re-select it with.
    const remaining = ids.filter((id) => id !== option._id);
    const included = siblings.find(
      (other) => other._id !== option._id && isIncludedOption(other)
    );
    if (included && !remaining.includes(included._id)) {
      return remaining.concat(included._id);
    }
    return remaining;
  }

  const siblingIds = siblings.map((other) => other._id);
  return ids.filter((id) => !siblingIds.includes(id)).concat(option._id);
}
